import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const DuplicateDetectorViewResult = ({result}) => {
  const formatMatchRow = (match) => (
    <div className="mb-2 p-2 bg-base-200 rounded-lg">
      <div className="flex items-center justify-between">
        <span className="font-medium">{match.value}</span>
        <div className="flex gap-2">
          <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
            {Math.round(match.confidence * 100)}%
          </span>
          <span className="px-2 py-0.5 rounded-full text-xs bg-info/20 text-info">
            {Math.round(match.similarity_score * 100)}% similar
          </span>
        </div>
      </div>
      {match.matches && match.matches.length > 0 && (
        <div className="text-sm mt-2 text-base-content/70">
          <span className="font-medium">Matches found in: </span>
          {match.matches.join(', ')}
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Matches Categories */}
        <div className="space-y-4">
          {/* Exact Matches */}
          {result?.duplicate_analysis?.exact_matches?.length > 0 && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-success/10">
                    <PhosphorIcons.Checks size={20} weight="bold" className="text-success"/>
                  </div>
                  <h3 className="card-title text-lg">Exact Matches</h3>
                </div>
                <div className="space-y-2">
                  {result.duplicate_analysis.exact_matches.map((match, index) => (
                    <div key={index}>{formatMatchRow(match)}</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Partial Matches */}
          {result?.duplicate_analysis?.partial_matches?.length > 0 && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-warning/10">
                    <PhosphorIcons.Textbox size={20} weight="bold" className="text-warning"/>
                  </div>
                  <h3 className="card-title text-lg">Partial Matches</h3>
                </div>
                <div className="space-y-2">
                  {result.duplicate_analysis.partial_matches.map((match, index) => (
                    <div key={index}>{formatMatchRow(match)}</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Semantic Matches */}
          {result?.duplicate_analysis?.semantic_matches?.length > 0 && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-info/10">
                    <PhosphorIcons.Brain size={20} weight="bold" className="text-info"/>
                  </div>
                  <h3 className="card-title text-lg">Semantic Matches</h3>
                </div>
                <div className="space-y-2">
                  {result.duplicate_analysis.semantic_matches.map((match, index) => (
                    <div key={index}>{formatMatchRow(match)}</div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Metadata Summary */}
        <div className="space-y-4">
          {/* Match Counts Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Match Summary</h3>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="stat bg-success/10 rounded-lg p-3">
                  <div className="stat-title text-success text-xs">Exact</div>
                  <div className="stat-value text-success text-2xl">
                    {result?.metadata?.match_counts?.by_category?.exact_matches || 0}
                  </div>
                </div>
                <div className="stat bg-warning/10 rounded-lg p-3">
                  <div className="stat-title text-warning text-xs">Partial</div>
                  <div className="stat-value text-warning text-2xl">
                    {result?.metadata?.match_counts?.by_category?.partial_matches || 0}
                  </div>
                </div>
                <div className="stat bg-info/10 rounded-lg p-3">
                  <div className="stat-title text-info text-xs">Semantic</div>
                  <div className="stat-value text-info text-2xl">
                    {result?.metadata?.match_counts?.by_category?.semantic_matches || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Similarity Distribution Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ChartPie size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Similarity Distribution</h3>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="stat bg-success/10 rounded-lg p-3">
                  <div className="stat-title text-success text-xs">High</div>
                  <div className="stat-value text-success text-2xl">
                    {result?.metadata?.similarity_summary?.high_similarity || 0}
                  </div>
                </div>
                <div className="stat bg-warning/10 rounded-lg p-3">
                  <div className="stat-title text-warning text-xs">Medium</div>
                  <div className="stat-value text-warning text-2xl">
                    {result?.metadata?.similarity_summary?.medium_similarity || 0}
                  </div>
                </div>
                <div className="stat bg-error/10 rounded-lg p-3">
                  <div className="stat-title text-error text-xs">Low</div>
                  <div className="stat-value text-error text-2xl">
                    {result?.metadata?.similarity_summary?.low_similarity || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Confidence Summary Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-success/10">
                  <PhosphorIcons.Gauge size={20} weight="bold" className="text-success"/>
                </div>
                <h3 className="card-title text-lg">Confidence Metrics</h3>
              </div>
              <div className="space-y-2">
                <div className="stat bg-primary/10 rounded-lg p-3">
                  <div className="stat-title text-primary text-xs">Average Confidence</div>
                  <div className="stat-value text-primary text-2xl">
                    {Math.round(result?.metadata?.confidence_summary?.average * 100)}%
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(result?.metadata?.confidence_summary?.by_category || {}).map(([category, confidence]) => (
                    <div key={category} className="p-2 bg-base-200 rounded-lg">
                      <div className="text-xs capitalize">{category.replace(/_/g, ' ')}</div>
                      <div className="text-lg font-bold">{Math.round(confidence * 100)}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateDetectorViewResult